"use client";

import { getCookie, setCookie } from "@/core/utils/cookies";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchExcess3Months } from "../services/excess-3-months-service";
import { createPlanReviewLead } from "../services/plan-review-lead-service";

export type ExceededCreditsState =
  | "initial"
  | "exceeded"
  | "sending"
  | "sent"
  | "analysis_started"
  | "completed";

const ANALYSIS_COOKIE_NAME = "exceeded_credits_analysis_started";
const LEAD_SENT_COOKIE_NAME = "exceeded_credits_review_sent";
const COOKIE_EXPIRATION_IN_DAYS = 30;

const getCookieExpirationDate = () => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + COOKIE_EXPIRATION_IN_DAYS);
  return expirationDate;
};

type OnActionClick = (
  event: keyof typeof import("@/_old/features/analytics/constants/events").EVENTS,
  category: keyof typeof import("@/_old/features/analytics/constants/events").EVENT_CATEGORIES,
) => void;

const EXCEEDED_CREDITS_VARIANT_KEY = "exceeded_credits_variant_index";

function getNextMessageVariantIndex(): number {
  const current = parseInt(
    sessionStorage.getItem(EXCEEDED_CREDITS_VARIANT_KEY) ?? "0",
    10,
  );
  const next = (current + 1) % 3;
  sessionStorage.setItem(EXCEEDED_CREDITS_VARIANT_KEY, String(next));
  return current;
}

export function useExceededCreditsCard({
  companyId,
  companyName,
  userId,
  userEmail,
  onActionClick,
}: {
  companyId: number;
  companyName?: string;
  userId?: number;
  userEmail?: string;
  onActionClick: OnActionClick;
}) {
  const [translate] = useTranslation(["dashboard"]);
  const [state, setState] = useState<ExceededCreditsState>("initial");
  const [isLoading, setIsLoading] = useState(false);
  const [excessValue, setExcessValue] = useState<number>(0);
  const [excessPercentage, setExcessPercentage] = useState<number>(0);
  const [excessData, setExcessData] = useState<
    import("../services/excess-3-months-service").Excess3MonthsResponse | null
  >(null);
  const [messageVariantIndex, setMessageVariantIndex] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const hasTrackedSent = useRef(false);
  const hasTrackedCompleted = useRef(false);
  const hasIncrementedVariantForThisLoad = useRef(false);
  const prevCompanyIdRef = useRef(companyId);

  const hasLeadData = Boolean(companyId && companyName && userId && userEmail);

  useEffect(() => {
    if (prevCompanyIdRef.current !== companyId) {
      prevCompanyIdRef.current = companyId;
      hasIncrementedVariantForThisLoad.current = false;
    }

    const fetchData = async () => {
      if (!companyId) {
        setIsFetchingData(false);
        return;
      }

      setIsFetchingData(true);

      try {
        const data = await fetchExcess3Months(companyId);

        if (!data) {
          setExcessValue(0);
          setExcessPercentage(0);
          setExcessData(null);
          setMessageVariantIndex(0);
          setState("initial");
          hasIncrementedVariantForThisLoad.current = false;
          return;
        }

        const { last_month } = data;
        setExcessValue(last_month.excess_value);
        setExcessPercentage(last_month.excess_percentage);
        setExcessData(data);

        if (!hasIncrementedVariantForThisLoad.current) {
          hasIncrementedVariantForThisLoad.current = true;
          setMessageVariantIndex(getNextMessageVariantIndex());
        }
      } catch (error) {
        console.error("Error fetching excess 3 months:", error);
        setExcessValue(0);
        setExcessPercentage(0);
      } finally {
        setIsFetchingData(false);
      }
    };

    fetchData();
  }, [companyId]);

  useEffect(() => {
    if (state === "sending" || state === "sent") {
      return;
    }
    if (isFetchingData) return;

    if (getCookie(LEAD_SENT_COOKIE_NAME) === "true") {
      hasTrackedSent.current = true;
      setState("sent");
      return;
    }

    const analysisCookie = getCookie(ANALYSIS_COOKIE_NAME);
    if (analysisCookie === "true") {
      setState("analysis_started");
      return;
    }

    if (excessValue > 0 || excessPercentage > 0) {
      setState("exceeded");
    }
  }, [state, excessValue, excessPercentage, isFetchingData, onActionClick]);

  useEffect(() => {
    if (state === "sent" && !hasTrackedSent.current) {
      onActionClick("LEAD_SENT_TO_CLICKUP_HIGH_CONSUMPTION", "DASHBOARD");
      hasTrackedSent.current = true;

      const timeoutId = setTimeout(() => {
        setState("analysis_started");
        setCookie(ANALYSIS_COOKIE_NAME, "true", {
          expirationDate: getCookieExpirationDate(),
        });
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [state, onActionClick]);

  useEffect(() => {
    if (state === "completed" && !hasTrackedCompleted.current) {
      onActionClick("UPGRADE_CONFIRMED", "DASHBOARD");
      hasTrackedCompleted.current = true;
    }
  }, [onActionClick]);

  const handleReviewConsumption = async () => {
    const canSendLead =
      (state === "exceeded" || state === "analysis_started") &&
      hasLeadData &&
      !isLoading;

    if (!canSendLead) {
      return;
    }

    onActionClick("CTA_CLICK_PLAN_REVIEW", "DASHBOARD");

    flushSync(() => {
      setState("sending");
      setIsLoading(true);
    });

    try {
      await createPlanReviewLead({
        companyId,
        companyName,
        userId,
        userEmail,
      });

      setCookie(LEAD_SENT_COOKIE_NAME, "true", {
        expirationDate: getCookieExpirationDate(),
      });

      flushSync(() => {
        setState("sent");
      });
      toast.success(translate("exceeded-credits-toast-success"));
    } catch (error) {
      console.error("Erro ao enviar lead para revisão de plano", error);
      flushSync(() => {
        setState("initial");
      });
      toast.error(translate("exceeded-credits-toast-error"));
    } finally {
      setIsLoading(false);
    }
  };

  const creditsData = {
    excessValue,
    excessPercentage,
    messageVariantIndex,
    status: state,
  };

  return {
    state,
    isLoading: isLoading || isFetchingData,
    isExceededCardDataLoading: isFetchingData,
    excessValue,
    excessPercentage,
    excessData,
    messageVariantIndex,
    creditsData,
    handleReviewConsumption,
    isLeadDataMissing: !hasLeadData,
  };
}
